import { Outlet, redirect } from 'react-router-dom'
import type { RouteObject } from 'react-router-dom'

import { paths } from '@/paths'
import { lazyWithErrorHandling } from '@/lib/lazy-with-error-handling'
import { Layout as DashboardLayout } from '@/components/dashboard/layout/layout'
import { OperatorRequired } from '@/components/dashboard/layout/operator-required'
import { Layout as SettingsLayout } from '@/components/dashboard/settings/layout'

export const route: RouteObject = {
	path: 'dashboard',
	element: (
		<DashboardLayout>
			<Outlet />
		</DashboardLayout>
	),
	children: [
		{
			element: (
				<OperatorRequired pageName="the overview page">
					<Outlet />
				</OperatorRequired>
			),
			children: [
				{
					index: true,
					handle: { pageName: 'Dashboard Overview' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/overview')),
				},
			],
		},
		{
			path: 'health',
			handle: { pageName: 'Health' },
			lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/health')),
		},
		{
			path: 'health/catalog',
			handle: { pageName: 'Health Alert Catalog' },
			lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/health/catalog')),
		},
		{
			path: 'analytics',
			element: (
				<OperatorRequired pageName="analytics">
					<Outlet />
				</OperatorRequired>
			),
			children: [
				{
					index: true,
					handle: { pageName: 'Analytics Collections' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/analytics/collections')),
				},
				{
					path: 'inventory',
					handle: { pageName: 'Analytics Inventory' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/analytics/inventory')),
				},
				{
					path: 'sales',
					handle: { pageName: 'Analytics Sales' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/analytics/sales')),
				},
				{
					path: 'daily-sales',
					handle: { pageName: 'Analytics Daily Sales' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/analytics/daily-sales')),
				},
				{
					path: 'tax-report',
					handle: { pageName: 'Analytics Tax Report' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/analytics/tax-report')),
				},
				{
					path: 'payouts',
					handle: { pageName: 'Analytics Payouts' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/analytics/payouts')),
				},
				{
					path: 'earnings',
					handle: { pageName: 'Analytics Earnings' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/analytics/earnings')),
				},
				{
					path: 'restock-audit',
					handle: { pageName: 'Analytics Restock Audit' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/analytics/restock-audit')),
				},
				{
					path: ':dashboardId',
					handle: { pageName: 'Analytics Dashboard' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/analytics/dashboard')),
				},
			],
		},
		{
			path: 'inventory',
			children: [
				{
					index: true,
					handle: { pageName: 'Inventory List' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/inventory/list')),
				},
			],
		},
		{
			path: 'products',
			children: [
				{
					index: true,
					handle: { pageName: 'Products List' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/products/list')),
				},
				{
					path: 'add',
					handle: { pageName: 'Products Add' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/products/list')),
				},
				{
					path: 'pricing',
					handle: { pageName: 'Products Pricing' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/products/list')),
				},
				{
					path: 'create',
					handle: { pageName: 'Product Create' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/products/create')),
				},
				{
					path: ':productId',
					handle: { pageName: 'Product Page' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/products/details')),
				},
			],
		},
		{
			path: 'stores',
			children: [
				{
					index: true,
					handle: { pageName: 'Stores List' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/stores/list')),
				},
				{
					path: 'create',
					handle: { pageName: 'Store Create' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/stores/create')),
				},
				{
					path: ':storeId/top-display-preview',
					handle: { pageName: 'Top Display Preview' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/stores/top-display-preview')),
				},
				{
					path: ':storeId',
					handle: { pageName: 'Store Page' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/stores/details')),
				},
				{
					path: ':storeId/edit',
					handle: { pageName: 'Store Edit' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/stores/edit')),
				},
				{
					path: ':storeId/cabinets/create',
					handle: { pageName: 'Cabinet Create' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/stores/cabinet/create')),
				},
				{
					path: ':storeId/cabinets/:cabinetId',
					handle: { pageName: 'Cabinet Page' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/stores/cabinet/details')),
				},
				{
					path: ':storeId/cabinets/:cabinetId/edit',
					handle: { pageName: 'Cabinet Edit' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/stores/cabinet/edit')),
				},
				{
					path: ':storeId/cabinets/:cabinetId/assign_price_tags',
					handle: { pageName: 'Cabinet Price Tags' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/stores/cabinet/configure-price-tags')),
				},
				{
					path: ':storeId/cabinets/:cabinetId/planogram/create',
					handle: { pageName: 'Planogram Create' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/stores/cabinet/planogram/create')),
				},
				{
					path: ':storeId/cabinets/:cabinetId/planogram/:planogramId',
					handle: { pageName: 'Planogram Edit' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/stores/cabinet/planogram/edit')),
				},
				{
					path: ':storeId/cabinets/:cabinetId/planogram/:planogramId/new',
					handle: { pageName: 'Planogram New' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/stores/cabinet/planogram/edit')),
				},
				{
					path: ':storeId/cabinets/:cabinetId/pricing',
					handle: { pageName: 'Cabinet Pricing' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/stores/cabinet/details')),
				},
			],
		},
		{
			path: 'planograms',
			children: [
				{
					index: true,
					handle: { pageName: 'Planograms List' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/planograms/list')),
				},
			],
		},
		{
			path: 'transactions',
			children: [
				{
					index: true,
					handle: { pageName: 'Transactions List' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/transactions/list')),
				},
			],
		},
		{
			path: 'marketing/canva',
			element: (
				<OperatorRequired pageName="Design with Canva">
					<Outlet />
				</OperatorRequired>
			),
			children: [
				{
					index: true,
					handle: { pageName: 'Marketing Canva' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/marketing/canva')),
				},
			],
		},
		{
			path: 'marketing',
			lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/marketing/list')),
			children: [
				{
					index: true,
					handle: { pageName: 'Marketing List' },
				},
				{
					path: 'folders',
					handle: { pageName: 'Marketing Folders' },
				},
				{
					path: 'folders/:folderId',
					handle: { pageName: 'Marketing Folder' },
				},
				{
					path: ':assetId/edit',
					handle: { pageName: 'Marketing Asset Edit' },
				},
				{
					path: ':assetId/delete',
					handle: { pageName: 'Marketing Asset Delete' },
				},
			],
		},
		{
			path: 'finances',
			children: [
				{
					index: true,
					handle: { pageName: 'Finances List' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/finances/list')),
				},
			],
		},
		{
			path: 'copilot',
			children: [
				{
					index: true,
					handle: { pageName: 'Copilot' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/chat/list')),
				},
			],
		},
		{
			path: 'restocks',
			children: [
				{
					index: true,
					handle: { pageName: 'Restocks List' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/restocks/list')),
				},
				{
					path: 'new',
					handle: { pageName: 'Restock New' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/restocks/new')),
				},
				{
					path: 'new/:storeId/cabinets',
					handle: { pageName: 'Restock Select Cabinets' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/restocks/new/cabinets')),
				},
				{
					path: 'new/:storeId/cabinets/:cabinetId/:restockId',
					handle: { pageName: 'Restock Cabinet' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/restocks/new/cabinet')),
				},
				{
					path: 'new/:storeId/cabinets/:cabinetId/:restockId/summary',
					handle: { pageName: 'Restock Summary' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/restocks/new/summary')),
				},
				{
					path: ':restockId',
					handle: { pageName: 'Restock Page' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/restocks/details')),
				},
			],
		},
		{
			path: 'settings',
			element: (
				<SettingsLayout>
					<Outlet />
				</SettingsLayout>
			),
			children: [
				{
					path: 'account',
					handle: { pageName: 'Settings Account' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/account')),
				},
				{
					path: 'team',
					handle: { pageName: 'Settings Team' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/team')),
				},
				{
					path: 'support',
					handle: { pageName: 'Settings Support' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/support')),
				},
				{
					path: 'cabinets',
					handle: { pageName: 'Settings Cabinets' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/terminal')),
				},
				{
					path: 'notifications',
					handle: { pageName: 'Settings Notifications' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/notifications')),
				},
				{
					path: 'api',
					handle: { pageName: 'Settings API' },
					// Legacy URL — the API page now lives under Integrations.
					loader: () => redirect(paths.dashboard.settings.api),
				},
				{
					path: 'integrations',
					children: [
						{
							index: true,
							handle: { pageName: 'Settings Integrations' },
							lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/integrations')),
						},
						{
							path: 'quickbooks',
							handle: { pageName: 'Settings QuickBooks Online' },
							lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/quickbooks')),
						},
						{
							path: 'google-business-profile',
							handle: { pageName: 'Settings Google Business Profile' },
							lazy: lazyWithErrorHandling(
								() => import('@/pages/dashboard/settings/integrations/google-business-profile')
							),
						},
						{
							path: 'canva',
							handle: { pageName: 'Settings Canva' },
							lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/integrations/canva')),
						},
						{
							path: 'api',
							handle: { pageName: 'Settings API' },
							lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/api')),
						},
						{
							path: 'mcp',
							handle: { pageName: 'Settings MCP Server' },
							lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/mcp')),
						},
						{
							path: 'vdi',
							handle: { pageName: 'Settings VDI' },
							lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/integrations/vdi')),
						},
						{
							path: 'ai-search',
							handle: { pageName: 'Settings AI Search' },
							lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/integrations/ai-search')),
						},
						{
							path: 'revenue-protect',
							handle: { pageName: 'Settings Revenue Protect' },
							lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/integrations/revenue-protect')),
						},
						{
							path: 'guardian',
							handle: { pageName: 'Settings Guardian' },
							lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/integrations/guardian')),
						},
						{
							path: 'shield',
							handle: { pageName: 'Settings Micromart Shield' },
							lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/integrations/shield')),
						},
						{
							path: 'interac',
							handle: { pageName: 'Settings Interac Debit' },
							lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/integrations/interac')),
						},
						{
							path: 'assistant',
							handle: { pageName: 'Settings Assistant' },
							lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/integrations/assistant')),
						},
						{
							path: 'foot-traffic',
							handle: { pageName: 'Settings Foot Traffic' },
							lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/integrations/foot-traffic')),
						},
					],
				},
				{
					path: 'taxes',
					children: [
						{
							index: true,
							handle: { pageName: 'Settings Taxes' },
							lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/taxes')),
						},
						{
							path: 'onboarding',
							handle: { pageName: 'Settings Tax Onboarding' },
							lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/taxes/onboarding')),
						},
						{
							path: 'regions/:regionId',
							handle: { pageName: 'Settings Tax Region' },
							lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/taxes/region-details')),
						},
						{
							path: 'groups/:groupId',
							handle: { pageName: 'Settings Tax Group' },
							lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/settings/taxes/group-details')),
						},
					],
				},
			],
		},
		{
			path: 'help/typeform/:formId',
			handle: { pageName: 'Help Typeform' },
			lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/help/typeform')),
		},
		{
			path: 'vdi',
			// Legacy URL — the VDI page now lives under Integrations.
			loader: () => redirect(paths.dashboard.settings.vdi),
		},
		{
			path: 'ai-search',
			// Legacy URL — the AI search page now lives under Integrations.
			loader: () => redirect(paths.dashboard.settings.aiSearch),
		},
		{
			path: 'market-insights',
			handle: { pageName: 'Market Insights' },
			lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/market-insights/layout')),
			children: [
				{
					index: true,
					handle: { pageName: 'Market Insights Overview' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/market-insights/overview')),
				},
				{
					path: 'recommendations',
					handle: { pageName: 'Market Insights Recommendations' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/market-insights/recommendations')),
				},
				{
					path: 'products',
					handle: { pageName: 'Market Insights Products' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/market-insights/products')),
				},
				{
					path: 'stores',
					handle: { pageName: 'Market Insights Stores' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/market-insights/stores')),
				},
				{
					path: 'pricing',
					handle: { pageName: 'Market Insights Pricing' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/market-insights/pricing')),
				},
				{
					path: 'planograms',
					handle: { pageName: 'Market Insights Planograms' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/market-insights/planograms')),
				},
			],
		},
		{
			path: 'location-insights',
			handle: { pageName: 'Location Insights' },
			lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/location-insights/layout')),
			children: [
				{
					index: true,
					handle: { pageName: 'Location Insights Overview' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/location-insights/overview')),
				},
				{
					path: 'traffic',
					handle: { pageName: 'Location Insights Traffic' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/location-insights/traffic')),
				},
				{
					path: 'audience',
					handle: { pageName: 'Location Insights Audience' },
					lazy: lazyWithErrorHandling(() => import('@/pages/dashboard/location-insights/audience')),
				},
			],
		},
		{
			path: 'promotions',
			children: [
				{
					path: '',
					handle: { pageName: 'Promotions List' },
					lazy: async () => {
						const { Page } = await import('@/pages/dashboard/promotions/list')
						return { Component: Page }
					},
				},
				{
					path: 'create',
					handle: { pageName: 'Promotion Create' },
					lazy: async () => {
						const { Page } = await import('@/pages/dashboard/promotions/create')
						return { Component: Page }
					},
				},
				{
					path: ':promotionId',
					handle: { pageName: 'Promotion Page' },
					lazy: async () => {
						const { Page } = await import('@/pages/dashboard/promotions/edit')
						return { Component: Page }
					},
				},
			],
		},
	],
}
